import { useState } from 'react';
import type { ActiveOffer } from 'types/api.generated';
import { useOfferCountdown } from 'shared/hooks/useOfferCountdown';
import { formatDistanceToPickup } from 'shared/lib/formatDistance';

type Offer = NonNullable<ActiveOffer>;

interface OfferCardProps {
  offer: Offer;
  onAccept: () => void;
  onDecline: () => void;
  onExpired: () => void;
  accepting?: boolean;
  declining?: boolean;
}

export default function OfferCard({
  offer,
  onAccept,
  onDecline,
  onExpired,
  accepting,
  declining,
}: OfferCardProps) {
  const [expiredLocally, setExpiredLocally] = useState(false);
  const countdown = useOfferCountdown(offer.expiresAt, () => {
    setExpiredLocally(true);
    onExpired();
  });

  const order = offer.order;
  const distanceLabel = formatDistanceToPickup(offer.distanceMeters);

  if (expiredLocally || !countdown) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-sm text-slate-600">
        Offer expired — waiting for the next request…
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-primary/30 rounded-2xl p-5 shadow-sm space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold text-primary uppercase tracking-widest">New request</p>
          <p className="text-lg font-black text-slate-900 mt-1">₦{order.amount.toLocaleString()}</p>
        </div>
        <span className="text-sm font-bold text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl tabular-nums">
          {countdown}
        </span>
      </div>

      <div className="space-y-2 text-sm">
        <p className="text-slate-700">
          <span className="font-semibold text-slate-500">Pickup:</span> {order.pickupAddress}
        </p>
        <p className="text-slate-700">
          <span className="font-semibold text-slate-500">Drop-off:</span> {order.deliveryAddress}
        </p>
        <p className="text-slate-500">{order.packageDescription}</p>
        {offer.distanceMeters != null && (
          <p className="text-xs font-semibold text-primary">
            {distanceLabel} away
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onAccept}
          disabled={accepting || declining}
          className="flex-1 min-w-[120px] bg-primary text-white font-bold py-3 rounded-xl text-sm disabled:opacity-50"
        >
          {accepting ? 'Accepting…' : 'Accept'}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={accepting || declining}
          className="flex-1 min-w-[120px] border-2 border-slate-200 font-bold py-3 rounded-xl text-sm text-slate-700 disabled:opacity-50"
        >
          {declining ? 'Declining…' : 'Decline'}
        </button>
      </div>
    </div>
  );
}
